import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

const PROFILE_COLUMNS = "id, full_name, email, phone, contact_pref";

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  // Remember which user the loaded profile belongs to, so a freshly signed-in
  // user isn't treated as "no profile" before their row has been fetched.
  const [profileState, setProfileState] = useState({
    userId: null,
    data: null,
  });

  const user = session?.user ?? null;

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  const loadProfile = useCallback(async (userId) => {
    const { data, error } = await supabase
      .from("profiles")
      .select(PROFILE_COLUMNS)
      .eq("id", userId)
      .maybeSingle();
    if (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to load profile", error);
    }
    setProfileState({ userId, data: data ?? null });
  }, []);

  // Fetch the profile whenever the signed-in user changes. Done outside the
  // auth listener because Supabase calls made inside it can deadlock.
  useEffect(() => {
    if (user?.id) loadProfile(user.id);
  }, [user?.id, loadProfile]);

  const profile = user && profileState.userId === user.id ? profileState.data : null;
  const loading = sessionLoading || Boolean(user && profileState.userId !== user.id);

  const updateProfile = async (changes) => {
    const { data, error } = await supabase
      .from("profiles")
      .update(changes)
      .eq("id", user.id)
      .select(PROFILE_COLUMNS)
      .single();
    if (error) throw error;
    setProfileState({ userId: user.id, data });
    return data;
  };

  const value = {
    session,
    user,
    profile,
    loading,
    // Google sign-ups arrive without a phone number, and the shop needs one
    // to reach customers about their orders.
    needsProfile: Boolean(user && !loading && !profile?.phone),
    updateProfile,
    signOut: () => supabase.auth.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
